import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  Stack,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Lock as LockIcon,
  Backspace as BackspaceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getKioskEmployeePreview, createAttendanceRecord } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import KioskStaffAuthDialog from '../components/KioskStaffAuthDialog';
import {
  enableKioskLock,
  endKioskSession,
  isKioskUnlocked,
  setKioskUnlocked,
} from '../utils/kioskSession';

const KIOSK_BRANCH_ID = process.env.REACT_APP_KIOSK_BRANCH_ID || '';
const KIOSK_SECRET = process.env.REACT_APP_KIOSK_SECRET;

const AttendanceKiosk = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [staffReady, setStaffReady] = useState(isKioskUnlocked);
  const [clock, setClock] = useState(new Date());
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [preview, setPreview] = useState(null);
  const [lastSuccess, setLastSuccess] = useState(null);
  const [punching, setPunching] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  useEffect(() => {
    if (staffReady) {
      enableKioskLock();
    }
  }, [staffReady]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const num = employeeNumber.trim();
    if (!num) {
      setPreview(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const params = { employeeNumber: num };
        if (KIOSK_BRANCH_ID) params.branchId = KIOSK_BRANCH_ID;
        const { data } = await getKioskEmployeePreview(params);
        setPreview(data);
      } catch {
        setPreview(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [employeeNumber]);

  const canPunch =
    preview && preview.firstName && preview.status === 'active';

  const appendDigit = (d) => {
    setEmployeeNumber((prev) => (prev + d).slice(0, 12));
    setLastSuccess(null);
  };

  const clearNumber = () => {
    setEmployeeNumber('');
    setPreview(null);
    setLastSuccess(null);
  };

  const backspace = () => {
    setEmployeeNumber((prev) => prev.slice(0, -1));
    setLastSuccess(null);
  };

  const submitPunch = async (kind) => {
    const num = employeeNumber.trim();
    if (!num || !canPunch || punching) return;
    setPunching(true);
    setLastSuccess(null);
    try {
      const body = { employeeNumber: num, kind };
      if (KIOSK_BRANCH_ID) body.branchId = KIOSK_BRANCH_ID;
      const { data } = await createAttendanceRecord(body);
      const label = kind === 'entrada' ? 'Entrada' : 'Salida';
      setLastSuccess({
        kind: label,
        name: `${preview.firstName} ${preview.lastName}`,
        time: data?.punchedAt ? new Date(data.punchedAt) : new Date(),
      });
      setEmployeeNumber('');
      setPreview(null);
    } catch (error) {
      const msg = error.response?.data?.message || 'No se pudo registrar la asistencia';
      setLastSuccess({ error: msg });
    } finally {
      setPunching(false);
    }
  };

  const handleStaffUnlock = () => {
    setKioskUnlocked();
    setStaffReady(true);
    setEnterOpen(false);
    enableKioskLock();
  };

  const handleExitSuccess = () => {
    endKioskSession();
    setExitOpen(false);
    if (isAuthenticated) {
      navigate('/attendance', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  const handleCancelEnter = () => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  const kioskReady = isAuthenticated || KIOSK_SECRET;

  if (!staffReady) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8' }}>
        <KioskStaffAuthDialog
          open
          onClose={handleCancelEnter}
          onSuccess={handleStaffUnlock}
          title="Activar terminal de asistencia"
          description="Solo un administrador o encargado puede abrir esta pantalla. Si alguien dejó el sistema abierto, no podrá entrar sin tu contraseña."
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f0f4f8',
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="NEXO"
            style={{ height: 40, filter: 'brightness(0) invert(1)' }}
          />
          <Typography variant="h6" fontWeight={700}>
            Terminal de asistencia
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            {format(clock, 'HH:mm:ss')}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {format(clock, "EEEE d MMMM yyyy", { locale: es })}
          </Typography>
        </Box>
        <IconButton
          color="inherit"
          onClick={() => setExitOpen(true)}
          aria-label="Salir del terminal"
          sx={{ border: '1px solid rgba(255,255,255,0.35)' }}
        >
          <LockIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 4 },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: '100%',
            maxWidth: 720,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
          }}
        >
          {!kioskReady && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Configura <strong>REACT_APP_KIOSK_SECRET</strong> en el frontend (igual que{' '}
              <strong>KIOSK_SECRET</strong> en el servidor) o inicia sesión como encargado antes de
              usar la tablet.
            </Alert>
          )}

          <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
            Ingresa tu número de empleado
          </Typography>

          <TextField
            fullWidth
            value={employeeNumber}
            onChange={(e) => {
              setEmployeeNumber(e.target.value.replace(/\D/g, '').slice(0, 12));
              setLastSuccess(null);
            }}
            placeholder="Ej. 1001"
            inputProps={{
              inputMode: 'numeric',
              style: { fontSize: '2rem', textAlign: 'center', letterSpacing: '0.15em' },
            }}
            InputProps={{
              endAdornment: employeeNumber ? (
                <InputAdornment position="end">
                  <IconButton onClick={backspace} edge="end" aria-label="Borrar">
                    <BackspaceIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 2 }}
          />

          <GridPad onDigit={appendDigit} onClear={clearNumber} onBackspace={backspace} />

          <Box sx={{ minHeight: 72, mt: 2, mb: 2 }}>
            {canPunch && (
              <Alert severity="info" icon={false}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {preview.firstName} {preview.lastName}
                </Typography>
                <Typography variant="body2">
                  {preview.position}
                  {preview.branch?.name ? ` · ${preview.branch.name}` : ''}
                </Typography>
              </Alert>
            )}
            {preview === false && employeeNumber.trim() && (
              <Alert severity="error">Número no encontrado. Revisa con recursos humanos.</Alert>
            )}
            {preview && preview.status !== 'active' && employeeNumber.trim() && (
              <Alert severity="warning">
                Empleado no activo. No se puede registrar asistencia.
              </Alert>
            )}
            {lastSuccess && !lastSuccess.error && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <strong>{lastSuccess.kind}</strong> registrada — {lastSuccess.name}
                <br />
                {format(lastSuccess.time, 'dd/MM/yyyy HH:mm:ss')}
              </Alert>
            )}
            {lastSuccess?.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {lastSuccess.error}
              </Alert>
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              disabled={!canPunch || punching || !kioskReady}
              startIcon={<LoginIcon />}
              onClick={() => submitPunch('entrada')}
              sx={{ py: 2, fontSize: '1.15rem' }}
            >
              Entrada
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={!canPunch || punching || !kioskReady}
              startIcon={<LogoutIcon />}
              onClick={() => submitPunch('salida')}
              sx={{ py: 2, fontSize: '1.15rem' }}
            >
              Salida
            </Button>
          </Stack>
        </Paper>
      </Box>

      <KioskStaffAuthDialog
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        onSuccess={handleExitSuccess}
        title="Salir del modo terminal"
        description="Confirma con tu cuenta de administrador o encargado para cerrar la pantalla de la tablet."
      />
    </Box>
  );
};

function GridPad({ onDigit, onClear, onBackspace }) {
  const keys = [
    { label: '1', action: () => onDigit('1') },
    { label: '2', action: () => onDigit('2') },
    { label: '3', action: () => onDigit('3') },
    { label: '4', action: () => onDigit('4') },
    { label: '5', action: () => onDigit('5') },
    { label: '6', action: () => onDigit('6') },
    { label: '7', action: () => onDigit('7') },
    { label: '8', action: () => onDigit('8') },
    { label: '9', action: () => onDigit('9') },
    { label: 'C', action: onClear },
    { label: '0', action: () => onDigit('0') },
    { label: '⌫', action: onBackspace },
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 1,
        maxWidth: 360,
        mx: 'auto',
      }}
    >
      {keys.map((key) => (
        <Button
          key={key.label}
          variant="outlined"
          size="large"
          onClick={key.action}
          sx={{
            py: 2,
            fontSize: key.label.length === 1 ? '1.5rem' : '1rem',
            minHeight: 56,
          }}
        >
          {key.label}
        </Button>
      ))}
    </Box>
  );
}

export default AttendanceKiosk;
